import {
  dimBindingProps,
  type DimBinding,
  type DimShape,
} from "sketch/shapes/dim";
import {
  BindingUtil,
  type BindingOnCreateOptions,
  type BindingOnShapeChangeOptions,
} from "tldraw";

import { syncDimValue } from "@/sketch/dim-drive";
import { dimEndpointPage } from "@/sketch/dim-geometry";

/**
 * Keeps a dim's stored endpoints matching its bound targets: when a target
 * moves or the binding is created, the resolved anchor point is written back
 * into the dim's props. That bumps the dim record, so geometry recomputes,
 * remote peers see the move, and the agent's projection reads fresh points
 * even without an editor. Deleting a target leaves the last resolved point
 * behind as a free endpoint (the graceful degrade the design specifies).
 */
export class DimBindingUtil extends BindingUtil<DimBinding> {
  static override type = "dim" as const;
  static override props = dimBindingProps;

  override getDefaultProps(): Partial<DimBinding["props"]> {
    return { terminal: "start", anchor: { x: 0.5, y: 0.5 } };
  }

  override onAfterCreate({ binding }: BindingOnCreateOptions<DimBinding>) {
    this.refresh(binding);
  }

  override onAfterChangeToShape({
    binding,
  }: BindingOnShapeChangeOptions<DimBinding>) {
    this.refresh(binding);
  }

  private refresh(binding: DimBinding) {
    const dim = this.editor.getShape<DimShape>(binding.fromId);
    if (!dim || !this.editor.getShapePageBounds(binding.toId)) return;
    // The resolved endpoint, not the raw anchor: a vertex- or edge-bound end
    // caches the point it really measures, so a reader without an editor
    // (the agent's projection) sees the same geometry the canvas draws.
    const point = dimEndpointPage(this.editor, dim, binding.props.terminal);
    const current = dim.props[binding.props.terminal];
    if (
      Math.abs(current.x - (point.x - dim.x)) < 1e-6 &&
      Math.abs(current.y - (point.y - dim.y)) < 1e-6
    ) {
      return;
    }
    this.editor.updateShape<DimShape>({
      id: dim.id,
      type: "dim",
      props: {
        [binding.props.terminal]: { x: point.x - dim.x, y: point.y - dim.y },
      },
    });
    // Two-way mirror: geometry moved, so an authored value follows it.
    syncDimValue(this.editor, dim.id);
  }
}
